import React from 'react';
import { Container } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FaGavel, FaUserPlus, FaUserShield, FaShieldAlt, FaLock, FaExclamationTriangle, FaCreditCard, FaUserTimes, FaBalanceScale, FaExclamationCircle, FaEdit, FaGlobe, FaEnvelope } from 'react-icons/fa';
import { Helmet } from 'react-helmet';

const TermsAndConditions = () => {
    const styles = {
        container: {
            maxWidth: '1000px',
            padding: '3rem 1rem',
            textAlign: 'left',
        },
        header: {
            textAlign: 'center',
            marginBottom: '3rem',
        },
        title: {
            fontSize: '2.5rem',
            fontWeight: '700',
            color: '#2C5282',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
        },
        lastUpdated: {
            color: '#718096',
            fontSize: '0.95rem',
            marginBottom: '2rem',
        },
        section: {
            marginBottom: '3rem',
            background: '#ffffff',
            borderRadius: '12px',
            padding: '2rem',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
        },
        sectionTitle: {
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#2D3748',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
        },
        sectionIcon: {
            color: '#4299E1',
            fontSize: '1.5rem',
        },
        text: {
            color: '#4A5568',
            lineHeight: '1.7',
            fontSize: '1rem',
            marginBottom: '1rem',
        },
        list: {
            listStyle: 'none',
            padding: 0,
            margin: '1rem 0 2rem',
        },
        listItem: {
            marginBottom: '0.75rem',
            paddingLeft: '1.5rem',
            position: 'relative',
            color: '#4A5568',
            '&::before': {
                content: '"•"',
                color: '#4299E1',
                position: 'absolute',
                left: 0,
            },
        },
        emphasis: {
            fontWeight: '600',
            color: '#2D3748',
        },
        warning: {
            background: '#FEF5F5',
            border: '1px solid #FED7D7',
            borderRadius: '8px',
            padding: '1.5rem',
            marginTop: '1rem',
        },
        warningTitle: {
            color: '#C53030',
            fontWeight: '600',
            marginBottom: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
        },
        warningIcon: {
            color: '#C53030',
            fontSize: '1.2rem',
        },
        contact: {
            background: '#F7FAFC',
            borderRadius: '8px',
            padding: '1.5rem',
            marginTop: '1rem',
        },
        link: {
            color: '#4299E1',
            textDecoration: 'none',
            transition: 'color 0.2s ease',
            '&:hover': {
                color: '#2B6CB0',
            },
        },
    };

    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                staggerChildren: 0.1,
            },
        },
    };

    const sectionVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 },
    };

    return (
        <>
            <Helmet>
                <title>DNACloudHub | Terms and Conditions</title>
                <meta name="description" content="Review the terms and conditions of using DNACloudHub, including your rights and responsibilities while accessing our cloud-based platform for running qpAdm models and genetic analysis." />
                <meta property="og:title" content={'DNACloudHub | Terms and Conditions'} />
                <meta property="og:description" content={'"Review the terms and conditions of using DNACloudHub, including your rights and responsibilities while accessing our cloud-based platform for running qpAdm models and genetic analysis."'} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={`https://dnacloudhub.com/terms_and_conditions`} />
            </Helmet>

            <Container style={styles.container}>
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={containerVariants}
                >
                    <div style={styles.header}>
                        <motion.h1 style={styles.title} variants={sectionVariants}>
                            <FaGavel size={32} color="#2C5282" />
                            Terms and Conditions
                        </motion.h1>
                        <p style={styles.lastUpdated}>Last Updated: 21st December 2024</p>
                    </div>

                    <motion.div style={styles.section} variants={sectionVariants}>
                        <h2 style={styles.sectionTitle}>
                            <FaBalanceScale style={styles.sectionIcon} />
                            1. Acceptance of Terms
                        </h2>
                        <p style={styles.text}>
                            By accessing or using DNACloudHub.com ("the Service"), you agree to comply with and be bound by these Terms and Conditions, along with our Privacy Policy. If you do not agree to these terms, you must not use the Service.
                        </p>
                    </motion.div>

                    <motion.div style={styles.section} variants={sectionVariants}>
                        <h2 style={styles.sectionTitle}>
                            <FaUserPlus style={styles.sectionIcon} />
                            2. User Registration and Account
                        </h2>
                        <p style={styles.text}>
                            To access and use the Service, you must create an account. By signing up, you agree to provide accurate, current, and complete information as required by the registration process, including:
                        </p>
                        <ul style={styles.list}>
                            {['First Name', 'Last Name', 'Username', 'Email Address', 'Role', 'Subscription Plan', 'Password', 'Ethnicity', 'Country'].map((item, index) => (
                                <li key={index} style={styles.listItem}>{item}</li>
                            ))}
                        </ul>
                        <p style={styles.text}>
                            You acknowledge that it is your responsibility to keep this information up to date. You are responsible for maintaining the confidentiality of your account information, including your username and password, and for all activities under your account.
                        </p>
                    </motion.div>

                    <motion.div style={styles.section} variants={sectionVariants}>
                        <h2 style={styles.sectionTitle}>
                            <FaUserShield style={styles.sectionIcon} />
                            3. User Responsibilities
                        </h2>
                        <p style={styles.text}>
                            You agree to use the Service solely for its intended purpose as a cloud-based platform for running QPADM processes. You are solely responsible for the content you upload, manage, and process within the Service, including ensuring that your data complies with all applicable laws, regulations, and third-party rights.
                        </p>
                    </motion.div>

                    <motion.div style={styles.section} variants={sectionVariants}>
                        <h2 style={styles.sectionTitle}>
                            <FaShieldAlt style={styles.sectionIcon} />
                            4. Privacy and Data Usage
                        </h2>
                        <p style={styles.text}>
                            We are committed to protecting your privacy and data. By signing up and using the Service, you consent to the collection and processing of your personal information as outlined in our Privacy Policy.
                        </p>
                        <div style={styles.warning}>
                            <h4 style={styles.warningTitle}>
                                <FaLock style={styles.warningIcon} />
                                Data Protection
                            </h4>
                            <p style={styles.text}>
                                We will not sell, rent, or lease your personal data to third parties. However, we may share data in accordance with the Privacy Policy or to comply with legal obligations.
                            </p>
                        </div>
                    </motion.div>

                    <motion.div style={styles.section} variants={sectionVariants}>
                        <h2 style={styles.sectionTitle}>
                            <FaExclamationTriangle style={styles.sectionIcon} />
                            5. Service Limitations
                        </h2>
                        <p style={styles.text}>
                            DNACloudHub.com provides only the infrastructure necessary to host and run QPADM processes in the cloud. We do not participate in the creation, management, or validation of any data you process within the Service.
                        </p>
                        <div style={styles.warning}>
                            <ul style={styles.list}>
                                <li style={styles.listItem}><strong>Accuracy of Results:</strong> We cannot and do not guarantee the accuracy, correctness, or completeness of any results.</li>
                                <li style={styles.listItem}><strong>No Warranty:</strong> The Service is provided "as is" and "as available" without warranties of any kind.</li>
                            </ul>
                        </div>
                    </motion.div>

                    <motion.div style={styles.section} variants={sectionVariants}>
                        <h2 style={styles.sectionTitle}>
                            <FaCreditCard style={styles.sectionIcon} />
                            6. Subscription Plans and Payment
                        </h2>
                        <p style={styles.text}>
                            The Service offers various subscription plans. By subscribing to a plan, you agree to pay all applicable fees based on your chosen subscription level. Subscription payments are non-refundable unless otherwise stated in the applicable plan or agreement.
                        </p>
                    </motion.div>

                    <motion.div style={styles.section} variants={sectionVariants}>
                        <h2 style={styles.sectionTitle}>
                            <FaUserTimes style={styles.sectionIcon} />
                            7. Account Termination
                        </h2>
                        <p style={styles.text}>
                            We reserve the right to suspend or terminate your account if we believe you have violated these Terms and Conditions or if you engage in activities that may harm the Service or other users. You may terminate your account at any time by contacting us through the provided support channels.
                        </p>
                    </motion.div>

                    <motion.div style={styles.section} variants={sectionVariants}>
                        <h2 style={styles.sectionTitle}>
                            <FaBalanceScale style={styles.sectionIcon} />
                            8. Indemnity
                        </h2>
                        <p style={styles.text}>
                            You agree to indemnify and hold harmless DNACloudHub.com, its affiliates, employees, agents, and contractors from any claims, damages, liabilities, losses, and expenses, including legal fees, arising out of or related to your use of the Service.
                        </p>
                    </motion.div>

                    <motion.div style={styles.section} variants={sectionVariants}>
                        <h2 style={styles.sectionTitle}>
                            <FaExclamationCircle style={styles.sectionIcon} />
                            9. Limitation of Liability
                        </h2>
                        <p style={styles.text}>
                            To the fullest extent permitted by law, DNACloudHub.com shall not be liable for any direct, indirect, incidental, special, consequential, or punitive damages arising out of your use or inability to use the Service, even if we have been advised of the possibility of such damages.
                        </p>
                    </motion.div>

                    <motion.div style={styles.section} variants={sectionVariants}>
                        <h2 style={styles.sectionTitle}>
                            <FaEdit style={styles.sectionIcon} />
                            10. Changes to the Terms
                        </h2>
                        <p style={styles.text}>
                            We reserve the right to modify, update, or change these Terms at any time. We will notify you of any changes by posting the updated Terms on the Service's website. By continuing to use the Service after such changes, you agree to be bound by the revised Terms and Conditions.
                        </p>
                    </motion.div>

                    <motion.div style={styles.section} variants={sectionVariants}>
                        <h2 style={styles.sectionTitle}>
                            <FaGlobe style={styles.sectionIcon} />
                            11. Governing Law
                        </h2>
                        <p style={styles.text}>
                            These Terms and Conditions shall be governed by and construed in accordance with the laws of [Jurisdiction], without regard to its conflict of laws principles.
                        </p>
                    </motion.div>

                    <motion.div style={styles.section} variants={sectionVariants}>
                        <h2 style={styles.sectionTitle}>
                            <FaEnvelope style={styles.sectionIcon} />
                            12. Contact Us
                        </h2>
                        <div style={styles.contact}>
                            <p style={styles.text}>If you have any questions or concerns regarding these Terms and Conditions, please contact us at:</p>
                            <p style={styles.text}><strong>Email:</strong> admin@dnacloudhub.com</p>
                            <p style={styles.text}><strong>Website:</strong> <a href="/contact_us" style={styles.link}>DNACloudHub.com Contact Page</a></p>
                        </div>
                    </motion.div>
                </motion.div>
            </Container>
        </>
    );
};

export default TermsAndConditions;
